import { useSyncExternalStore } from 'react';
import { session } from '~/session/session';
import { isNative } from './platform';
import { storage } from './storage';

/**
 * Face ID / fingerprint unlock for the native apps on personal phones. After a normal sign-in,
 * staff can turn it on in Me; from then on the app opens behind a lock screen that only their
 * face or fingerprint clears, on launch and after a few minutes in the background.
 * Not offered on front-desk devices, which are shared.
 */

/** How long the app may sit in the background before it locks again. */
const RELOCK_AFTER_MS = 2 * 60_000;

export interface Biometry {
  /** "Face ID", "Touch ID", "fingerprint"… for labels */
  label: string;
  face: boolean;
}

let biometry: Biometry | null | undefined;

/** What this device offers, or null (web, no hardware, or nothing enrolled). */
export async function getBiometry(): Promise<Biometry | null> {
  if (biometry !== undefined) return biometry;
  biometry = null;
  if (!isNative()) return biometry;
  try {
    const { NativeBiometric, BiometryType } = await import('@capgo/capacitor-native-biometric');
    const r = await NativeBiometric.isAvailable();
    if (r.isAvailable) {
      const labels: Partial<Record<number, string>> = {
        [BiometryType.FACE_ID]: 'Face ID',
        [BiometryType.TOUCH_ID]: 'Touch ID',
        [BiometryType.FINGERPRINT]: 'fingerprint',
        [BiometryType.FACE_AUTHENTICATION]: 'face unlock',
      };
      biometry = {
        label: labels[r.biometryType] ?? 'biometrics',
        face: r.biometryType === BiometryType.FACE_ID || r.biometryType === BiometryType.FACE_AUTHENTICATION,
      };
    }
  } catch {
    /* plugin unavailable */
  }
  return biometry;
}

/** Ask for Face ID / fingerprint. Resolves false when cancelled or not recognised. */
export async function verifyBiometric(reason: string): Promise<boolean> {
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    await NativeBiometric.verifyIdentity({
      reason,
      title: 'Unlock Hudhuria',
      negativeButtonText: 'Cancel',
      maxAttempts: 3,
    });
    return true;
  } catch {
    return false;
  }
}

// ---------- the setting (per device, for whoever is signed in) ----------

export const biometricEnabled = () => storage.get('biometric') === '1';

export function setBiometricEnabled(on: boolean) {
  storage.set('biometric', on ? '1' : null);
  notify();
}

// ---------- lock state ----------

let locked = false;
const listeners = new Set<() => void>();
const notify = () => {
  for (const l of listeners) l();
};

const shouldLock = () => isNative() && biometricEnabled() && !!session.get().token && !session.get().desk;

export const appLock = {
  isLocked: () => locked,
  lock() {
    if (!shouldLock() || locked) return;
    locked = true;
    notify();
  },
  unlock() {
    locked = false;
    notify();
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

export function useAppLocked() {
  return useSyncExternalStore(appLock.subscribe, appLock.isLocked);
}

export function useBiometricEnabled() {
  return useSyncExternalStore(appLock.subscribe, biometricEnabled);
}

/** Lock at launch, and again when the app returns after RELOCK_AFTER_MS in the background. */
export async function initAppLock() {
  if (!isNative()) return;
  appLock.lock();
  const { App } = await import('@capacitor/app');
  let hiddenAt = 0;
  void App.addListener('appStateChange', ({ isActive }) => {
    if (!isActive) hiddenAt = Date.now();
    else if (hiddenAt && Date.now() - hiddenAt > RELOCK_AFTER_MS) appLock.lock();
  });
}
